import { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormGroup,
  FormControlLabel,
  Checkbox,
  Box,
  Typography,
} from '@mui/material';

/**
 * FeedbackModal component - Modal for collecting negative feedback details
 *
 * @param {Object} props - Component props
 * @param {boolean} props.open - Whether the modal is open
 * @param {Function} props.onClose - Callback to close the modal
 * @param {Function} props.onSubmit - Callback when feedback is submitted
 * @returns {JSX.Element} FeedbackModal component
 */
const FeedbackModal = ({ open, onClose, onSubmit }) => {
  const [selectedReasons, setSelectedReasons] = useState([]);
  const [additionalFeedback, setAdditionalFeedback] = useState('');

  const feedbackReasons = [
    'Numbers are Wrong',
    'Incorrect Location(s)',
    'Bad Answer',
    'Hard to Read (formatting issues)',
    'Took Too Long',
    'Other',
  ];

  const handleReasonToggle = (reason) => {
    setSelectedReasons((prev) =>
      prev.includes(reason)
        ? prev.filter((r) => r !== reason)
        : [...prev, reason]
    );
  };

  const handleSubmit = () => {
    onSubmit({
      reasons: selectedReasons,
      additionalFeedback,
    });
    // Reset form
    setSelectedReasons([]);
    setAdditionalFeedback('');
    onClose();
  };

  const handleCancel = () => {
    // Reset form
    setSelectedReasons([]);
    setAdditionalFeedback('');
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleCancel} maxWidth="xs" fullWidth>
      <DialogTitle sx={{ paddingBottom: 0, fontSize: '1.125rem', }} >Help Zigy Improve</DialogTitle>
      <DialogContent>
        <Box>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            What could have been better about this response?
          </Typography>
          <FormGroup>
            {feedbackReasons.map((reason) => (
              <FormControlLabel
                key={reason}
                control={
                  <Checkbox
                    checked={selectedReasons.includes(reason)}
                    onChange={() => handleReasonToggle(reason)}
                    sx={{ padding: 0.5, marginLeft: -0.5 }}
                  />
                }
                label={reason}
              />
            ))}
          </FormGroup>
          <TextField
            label="Additional feedback (optional)"
            multiline
            rows={4}
            fullWidth
            value={additionalFeedback}
            onChange={(e) => setAdditionalFeedback(e.target.value)}
            sx={{ mt: 2 }}
            placeholder="Tell us more about what could be improved..."
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleCancel} color="inherit">
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={selectedReasons.length === 0}
        >
          Submit Feedback
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default FeedbackModal;
